import React, { useEffect, useRef } from "react";
import { Provider } from "react-redux";
import { BrowserRouter } from "react-router-dom";
import { ThemeSwitcherProvider } from "react-css-theme-switcher";
import { ConfigProvider, Empty, message, notification } from "antd";
import store from "./store";
import history from "./history";
import Layouts from "./layouts";
import { THEME_CONFIG, WS_ENDPOINT_URL } from "./configs/AppConfig";
import "./lang";
import { enablePushForAdmin } from "utils/pushFunction";
import PushNavListener from "PushNavListener";
import AdminWsListener from "AdminWsListener";
import { AdminNotificationsProvider } from "contexts/AdminNotificationsContext";
import { API_BASE_URL } from "constants/ApiConstant";
import { AUTH_TOKEN, USER_INFO } from "constants/AuthConstant";
import { getTokenExpiry } from "utils/jwt";
import { refreshAccessToken } from "auth/ApiCallInstance";
// import AdminWsBridge from "AdminWsBridge";
// import { DataProvider } from "contexts/DataProvider";
// import { WebSocketProvider } from "contexts/WebSocketProvider";

const themes = {
  dark: `${process.env.PUBLIC_URL}/css/dark-theme.css`,
  light: `${process.env.PUBLIC_URL}/css/light-theme.css`,
};

// Helper function to check and redirect kiosk users - call this anywhere needed
const checkAndRedirectKioskUser = () => {
  const currentPath = window.location.pathname;
  const hasToken = localStorage.getItem(AUTH_TOKEN);
  const storedUser = JSON.parse(localStorage.getItem(USER_INFO) || '{}');
  const isKioskUser = storedUser?.isKioskUser === true;
  const isKioskDevice = localStorage.getItem('is_kiosk_device') === 'true';
  
  // Skip if already on kiosk, portal, or auth routes
  if (currentPath.includes('/kiosk') || 
      currentPath.startsWith('/portal') || 
      currentPath.startsWith('/auth')) {
    return false;
  }
  
  // If logged in and is kiosk user/device, redirect to kiosk
  if (hasToken && (isKioskUser || isKioskDevice)) {
    if (isKioskUser) {
      localStorage.setItem('is_kiosk_device', 'true');
    }
    console.log('[App] Kiosk user detected, redirecting to /app/kiosk');
    window.location.href = '/app/kiosk';
    return true;
  }
  
  return false;
};

// Make it globally available for other components
window.__checkKioskRedirect = checkAndRedirectKioskUser;

function App() {
  const refreshInFlightRef = useRef(false);

  // Auto-redirect kiosk devices/users to kiosk route - runs on every render
  useEffect(() => {
    const currentPath = window.location.pathname;
    const hasToken = localStorage.getItem(AUTH_TOKEN);
    
    // If accessing kiosk route directly (including PWA start), mark this device as a kiosk
    if (currentPath.includes('/kiosk')) {
      localStorage.setItem('is_kiosk_device', 'true');
      // Store intended route for after login
      if (!hasToken) {
        sessionStorage.setItem('kiosk_intended_route', '/app/kiosk');
      }
      return; // Already on kiosk, no need to redirect
    }
    
    // Check and redirect if kiosk user
    if (checkAndRedirectKioskUser()) {
      return; // Redirecting, don't continue
    }
    
    // If just logged in with an intended route
    const intendedRoute = sessionStorage.getItem('kiosk_intended_route');
    if (hasToken && intendedRoute) {
      sessionStorage.removeItem('kiosk_intended_route');
      window.location.href = intendedRoute;
    }
  });
  
  // Also check on storage changes (in case user logs in from another tab)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === USER_INFO || e.key === AUTH_TOKEN) {
        checkAndRedirectKioskUser();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  useEffect(() => {
    // Skip push notifications for public portal routes and kiosk
    if (window.location.pathname.startsWith('/portal') || window.location.pathname.includes('/kiosk')) {
      return;
    }
    
    // ✅ keep auto-enable for desktop
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    if (!isIOS) {
      enablePushForAdmin().catch(console.error);
    }
  }, []);

  useEffect(() => {
    message.config({ maxCount: 3, duration: 3 });
    notification.config({ maxCount: 3, duration: 4, placement: "topRight" });
  }, []);

  useEffect(() => {
    const checkAndRefreshSession = async () => {
      // Skip session checks for public portal routes and kiosk
      if (window.location.pathname.startsWith('/portal') || window.location.pathname.includes('/kiosk')) {
        return;
      }
      
      const token = localStorage.getItem(AUTH_TOKEN);
      const expiry = getTokenExpiry(token);
      if (!expiry) return;
      const now = Date.now();
      const minutesLeft = Math.floor((expiry - now) / 60000);

      if (minutesLeft <= 5 && minutesLeft >= 0 && !refreshInFlightRef.current) {
        refreshInFlightRef.current = true;
        try {
          await refreshAccessToken();
        } catch (error) {
          // If refresh fails, interceptor will handle logout when request is made
        } finally {
          refreshInFlightRef.current = false;
        }
      }
    };

    checkAndRefreshSession();
    const interval = setInterval(checkAndRefreshSession, 60000);
    return () => clearInterval(interval);
  }, []);

  // useEffect(() => {
  //   const preventZoom = (event) => {
  //     if (event.ctrlKey || event.touches?.length > 1) {
  //       event.preventDefault();
  //     }
  //   };

  //   document.addEventListener("wheel", preventZoom, { passive: false });
  //   document.addEventListener("keydown", (event) => {
  //     if (event.ctrlKey && (event.key === "+" || event.key === "-")) {
  //       event.preventDefault();
  //     }
  //   });
  //   document.addEventListener("touchmove", preventZoom, { passive: false });

  //   return () => {
  //     document.removeEventListener("wheel", preventZoom);
  //     document.removeEventListener("keydown", preventZoom);
  //     document.removeEventListener("touchmove", preventZoom);
  //   };
  // }, []);

  return (
    <div className="App">
      <Provider store={store}>
        <BrowserRouter history={history}>
          <AdminNotificationsProvider>
            {/* ✅ NOW it can read context */}
            <AdminWsListener />

            <PushNavListener />
            <ThemeSwitcherProvider
              themeMap={themes}
              defaultTheme={THEME_CONFIG.currentTheme}
              insertionPoint="styles-insertion-point"
            >
              <ConfigProvider
                renderEmpty={() => (
                  <Empty
                    description="No data available yet"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                )}
              >
                <Layouts />
              </ConfigProvider>
            </ThemeSwitcherProvider>
          </AdminNotificationsProvider>
        </BrowserRouter>
      </Provider>
    </div>
  );
}

export default App;
